/*
  Delivery pincode check on the product page.

  The courier's serviceability list ships as a theme asset rather than living
  behind an API, because the answer never changes per visitor and a network
  round-trip on every keystroke would be slower and less reliable than the
  28,714-row lookup table itself. The file is fetched ONCE, lazily, the first
  time a shopper touches the field, so a page nobody checks a pincode on never
  downloads it.

  The list only ever narrows on lookup: a pincode that is not in it is not
  serviceable. That covers typos and codes the courier has never heard of,
  which is the behaviour we want.

  ENFORCEMENT. Add to cart stays visible and is refused when it is pressed,
  rather than being hidden until a pincode passes. A button that is simply
  absent teaches the shopper nothing - they leave assuming the page is broken.
  A button that answers back with "check your pincode first" sends them to the
  one field that will fix it.

  The refusal is a capture-phase submit listener on the document. Listener
  order on the form element itself is registration order regardless of the
  capture flag, so binding there would be a race against Dawn's product-form.js.
  On an ANCESTOR, capture always runs before anything on the target, so
  stopPropagation there reliably keeps Dawn's handler from ever seeing it.

  This product page has no Buy it now button - Add to cart is the only route
  into checkout - so that one interception is a complete gate.
*/
class EyewearPincode extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-pincode-form]');
    this.input = this.querySelector('[data-pincode-input]');
    this.result = this.querySelector('[data-pincode-result]');
    this.button = this.querySelector('[data-pincode-button]');
    // NOT this.hidden: HTMLElement.hidden is a built-in boolean, so assigning
    // an element to it coerces to true, stamps the hidden attribute on this
    // whole block, and makes every later .value write throw on a boolean.
    this.propertyInput = this.querySelector('[data-pincode-property]');
    this.changeBtn = this.querySelector('[data-pincode-change]');

    this.src = this.dataset.src;
    this.mode = this.dataset.gate || 'block';
    this.formId = this.dataset.form || '';
    this.storageKey = 'eyewear:pincode';

    this.table = null;
    this.loading = null;

    // Nothing has passed yet, so nothing may be bought yet.
    this.verified = false;
    // Set only when our own list fails to load - see load()/check().
    this.bypass = false;

    this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
  }

  connectedCallback() {
    if (!this.form || !this.input) return;

    if (this.button) {
      this.button.addEventListener('click', () => this.check(this.input.value, { announce: true }));
    }

    // The wrapper is a div, not a form, so Enter has to be wired by hand.
    // preventDefault matters: this field can sit inside the product form's
    // subtree, where an unhandled Enter submits the cart.
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.check(this.input.value, { announce: true });
    });

    // Warm the table on first contact so the answer feels instant when the
    // sixth digit lands. Nothing here blocks typing.
    this.input.addEventListener('focus', () => this.load().catch(() => {}), { once: true });

    this.input.addEventListener('input', () => {
      const digits = this.input.value.replace(/[^0-9]/g, '').slice(0, 6);
      if (digits !== this.input.value) this.input.value = digits;

      this.clear();
      if (digits.length === 6) this.check(digits, { announce: false });
    });

    if (this.changeBtn) {
      this.changeBtn.addEventListener('click', () => {
        this.clear();
        this.input.value = '';
        this.input.focus();
      });
    }

    if (this.mode !== 'off') {
      document.addEventListener('submit', this.onDocumentSubmit, true);
    }

    // Only the hide mode closes anything up front. In block mode the controls
    // stay exactly where they are and the refusal happens on press.
    if (this.mode === 'hide') this.setPurchaseVisible(false);

    // Auto-restoring the last-typed pincode from localStorage was filling
    // the field with a stale value on every visit, which read as a wrong
    // 'default'. The field now always starts empty; readSaved()/save() stay
    // defined below for a future opt-in, just not called here.
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.onDocumentSubmit, true);
  }

  /* ---------- enforcement ---------- */

  blocked() {
    return this.mode !== 'off' && !this.verified && !this.bypass;
  }

  /*
    NEVER read form.id here.

    A <form> exposes its named controls as own properties, and every Shopify
    add-to-cart form carries <input name="id"> for the variant. That input
    shadows the id IDL attribute, so form.id returns the ELEMENT, not the
    string - and any comparison against it silently fails, letting every add
    through. getAttribute is not shadowed.
  */
  targetsThisProduct(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (this.formId && form.getAttribute('id') === this.formId) return true;

    // Fallback for a renamed or missing form id: any cart form inside the same
    // product column is this product's.
    const action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') !== -1 && !!form.closest('.eyewear-product__info-container');
  }

  onDocumentSubmit(event) {
    if (!this.blocked()) return;
    if (!this.targetsThisProduct(event.target)) return;

    // preventDefault stops the browser's own submission; stopPropagation stops
    // the event ever reaching the capture listener in eyewear-cart.js or Dawn's
    // handler on the form. Both are needed - either alone still lets an
    // unserviceable order through one route.
    event.preventDefault();
    event.stopPropagation();
    this.refuse();
  }

  /*
    Say why, then put the cursor where the fix is. A refusal that only says no
    leaves the shopper hunting for the reason somewhere above the button.
  */
  refuse() {
    // An existing red line already names the problem ("we do not deliver to
    // 858434 yet"). Replacing it with the generic ask would be a step
    // backwards, so only fill an empty or neutral slot.
    const alreadyExplained = this.result && this.result.classList.contains('eyewear-pincode__result--error');
    if (!alreadyExplained) this.render('error', this.dataset.msgBlocked);

    this.classList.remove('eyewear-pincode--nudge');
    // Reading offsetWidth restarts the animation; without it a second press
    // in a row does nothing visible and reads as a dead button.
    void this.offsetWidth;
    this.classList.add('eyewear-pincode--nudge');

    if (typeof this.scrollIntoView === 'function') {
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    this.input.focus({ preventScroll: true });

    this.dispatch('eyewear:pincode:blocked', { pincode: this.input.value });
  }

  /* ---------- data ---------- */

  load() {
    if (this.table) return Promise.resolve(this.table);
    if (this.loading) return this.loading;

    this.loading = fetch(this.src)
      .then((response) => {
        if (!response.ok) throw new Error('pincode list ' + response.status);
        return response.json();
      })
      .then((payload) => {
        this.table = EyewearPincode.decode(payload);
        return this.table;
      })
      .catch((error) => {
        console.error('[eyewear-pincode]', error);
        this.loading = null;
        throw error;
      });

    return this.loading;
  }

  /*
    Records arrive as "<pincodeDelta>.<cityIndex>[!]", base 36, sorted, with a
    trailing "!" meaning prepaid only. Storing deltas rather than whole pincodes
    is what keeps a 28,714-row table down to a ~70 KB transfer.
  */
  static decode(payload) {
    const cities = payload.c || [];
    const map = new Map();
    let pin = 0;

    for (const record of (payload.d || '').split(',')) {
      if (!record) continue;
      const noCod = record.charCodeAt(record.length - 1) === 33; // "!"
      const body = noCod ? record.slice(0, -1) : record;
      const dot = body.indexOf('.');

      pin += parseInt(body.slice(0, dot), 36);
      map.set(pin, { c: cities[parseInt(body.slice(dot + 1), 36)] || '', cod: !noCod });
    }

    return map;
  }

  /* ---------- checking ---------- */

  check(value, options) {
    const opts = options || {};
    const pincode = String(value || '').replace(/[^0-9]/g, '');

    if (pincode.length !== 6) {
      if (opts.announce) this.render('error', this.dataset.msgInvalid);
      return;
    }

    this.setBusy(true);

    this.load()
      .then((table) => {
        this.setBusy(false);
        const hit = table.get(Number(pincode));
        if (hit) this.pass(pincode, hit, opts);
        else this.fail(pincode);
      })
      .catch(() => {
        this.setBusy(false);
        // Our own list failed to download. Refusing every sale on the site over
        // a CDN blip would cost far more than the rare unserviceable order, so
        // the gate stands down until the page is reloaded.
        this.bypass = true;
        this.setPurchaseVisible(true);
        this.render('error', this.dataset.msgError);
      });
  }

  pass(pincode, hit, opts) {
    const place = hit.c ? hit.c.split('|').filter(Boolean).join(', ') : '';
    let message = place
      ? this.template('msgSuccessCity', pincode, place)
      : this.template('msgSuccess', pincode);

    if (!hit.cod && this.dataset.msgNoCod) {
      message += ' ' + this.dataset.msgNoCod;
    }

    this.verified = true;
    this.render('success', message, { compact: true });
    this.setPurchaseVisible(true);
    if (this.propertyInput) this.propertyInput.value = pincode;
    this.save(pincode);

    if (!opts.restored) this.dispatch('eyewear:pincode:serviceable', { pincode: pincode, cod: hit.cod });
  }

  fail(pincode) {
    this.verified = false;
    this.render('error', this.template('msgUnserviceable', pincode));
    if (this.propertyInput) this.propertyInput.value = '';
    this.setPurchaseVisible(false);
    this.forget();
    this.dispatch('eyewear:pincode:unserviceable', { pincode: pincode });
  }

  /*
    Back to "no answer yet". In block mode that is a silent state - the buy
    controls are still on screen and will explain themselves if pressed, so
    putting a standing warning under a field the shopper is mid-way through
    typing would just be nagging.
  */
  clear() {
    this.verified = false;
    // Hide mode is the exception: with the button gone there is nothing left
    // to explain itself, so the ask has to stand in its place.
    const hiding = this.mode === 'hide';
    this.render(hiding ? 'prompt' : null, hiding ? this.dataset.msgRequired : '');
    if (this.propertyInput) this.propertyInput.value = '';
    this.setPurchaseVisible(!hiding);
  }

  /* ---------- output ---------- */

  render(state, message, options) {
    if (!this.result) return;
    this.result.textContent = message || '';
    this.result.hidden = !message;
    this.result.classList.toggle('eyewear-pincode__result--success', state === 'success');
    this.result.classList.toggle('eyewear-pincode__result--error', state === 'error');
    this.result.classList.toggle('eyewear-pincode__result--prompt', state === 'prompt');
    this.toggleAttribute('data-checked', state === 'success');
    if (this.changeBtn) this.changeBtn.hidden = !(options && options.compact);
  }

  setBusy(busy) {
    this.classList.toggle('eyewear-pincode--busy', !!busy);
    if (this.button) this.button.disabled = !!busy;
  }

  /*
    Only used by the hide mode. The quantity stepper goes with the button,
    since both live in this row and a lone stepper reads as a broken page.
  */
  setPurchaseVisible(visible) {
    if (this.mode !== 'hide') return;
    const scope = this.closest('.eyewear-product__info-container') || document;
    const row = scope.querySelector('.eyewear-purchase-row');
    if (row) row.toggleAttribute('data-pincode-blocked', !visible);
  }

  template(key, pincode, place) {
    return String(this.dataset[key] || '')
      .replace(/\[pincode\]/g, pincode || '')
      .replace(/\[place\]/g, place || '');
  }

  dispatch(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
  }

  /* ---------- memory ---------- */

  readSaved() {
    try {
      const saved = localStorage.getItem(this.storageKey) || '';
      return /^[1-9][0-9]{5}$/.test(saved) ? saved : '';
    } catch (error) {
      return '';
    }
  }

  save(pincode) {
    try {
      localStorage.setItem(this.storageKey, pincode);
    } catch (error) {
      /* private browsing - remembering is a convenience, not a requirement */
    }
  }

  forget() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      /* as above */
    }
  }
}

if (!customElements.get('eyewear-pincode')) {
  customElements.define('eyewear-pincode', EyewearPincode);
}
